<template>
  <CForm @submit="submit" :data="formData" vertical>
    <CModal
      :hasPadding="false"
      :title="$t('driveBooking.reservingReplacementTransport')"
      icon="car-arrows"
      full-height
      @close="close"
      :hideFooter="
        !(
          data.editableSettings?.isCommentEditable ||
          data.editableSettings?.isDriverEditable ||
          data.editableSettings?.isStartDateEditable ||
          data.editableSettings?.isEndDateEditable ||
          data.editableSettings?.isRequestedTypeEditable
        )
      "
    >
      <template #body>
        <div class="drive-reservation-modal-wrapper">
          <div class="datetime-wrapper">
            <CField
              type="datetime"
              :disabled="!data.editableSettings?.isStartDateEditable"
              :label="$t('driveBooking.startReservation')"
              :config="{ maxDate: maxEndDate?.toDate() }"
              v-model="formData.start"
              :validators="startDateValidation"
            />
            <CField
              type="datetime"
              :disabled="!data.editableSettings?.isEndDateEditable || !formData.start"
              :config="{
                minDate: minEndDate?.toDate(),
                minTime: minEndDateTime,
                startTime: endDateStartTime,
              }"
              :label="$t('driveBooking.endReservation')"
              v-model="formData.end"
              :validators="endDateValidation"
              @internal-value-change="handleDateInternalValueChange"
            />
          </div>
          <div class="message-wrapper" v-if="!formData.start || !formData.end">
            <p>{{ $t("driveBooking.selectDates") }}</p>
          </div>
          <div class="drive-form-wrapper" v-if="formData.start && formData.end">
            <div class="u-mt-5">
              <h2>{{ $t("driveBooking.driver") }}</h2>
              <CSelectedAssetDetails
                :name="`${data.renter?.firstName} ${data.renter?.lastName}`"
                @edit="handleDriverSectionExpand"
                @cancel="cancelDriverChange"
                :data="[
                  { label: $t('driveBooking.telephone'), value: data.renter?.phone },
                  { label: $t('driveBooking.email'), value: data.renter?.email },
                ]"
                :readonly="!data.editableSettings?.isDriverEditable"
                :isCancel="isDriverSectionExpanded"
              />
              <CFlex
                align="start"
                class="driver-dropdown-container"
                v-if="isDriverSectionExpanded"
              >
                <div class="driver-dropdown-wrapper">
                  <CField :validators="{ required }" v-model="selectedDriver">
                    <template #default="bindings">
                      <COptionsDropdown
                        :disabled="!data.editableSettings?.isDriverEditable"
                        fontWeight="normal"
                        :modelValue="bindings['modelValue']"
                        @update:modelValue="bindings['onUpdate:modelValue']"
                        :options="driveBookingContactsOptions"
                        :getLabel="(o) => o?.label"
                        :placeholder="`${$t('driveBooking.selectDriver')}...`"
                        :emptyOptionsMessage="$t('driveBooking.noContactsOptions')"
                      />
                    </template>
                  </CField>
                </div>
                <CButton
                  theme="normal-primary"
                  @click="() => ContactModals.contact(contactContext)"
                >
                  <CIcon name="person-plus" />
                  {{ $t("driveBooking.addContactButton") }}
                </CButton>
              </CFlex>

              <div
                v-if="shouldShowDriverWarningLabel"
                class="edit-reservation__tag-wrapper"
              >
                <CTag theme="warning-light">
                  <CIcon name="warning-alt" :size="25" />
                  {{
                    $t("driveBooking.switchedFrom", {
                      from: `${data.renter?.firstName} ${data.renter?.lastName}`,
                      to: selectedDriver.label,
                    })
                  }}
                </CTag>
              </div>
            </div>

            <div class="u-mt-5">
              <h2>{{ $t("driveBooking.remark") }}</h2>
              <CField
                type="text"
                v-model="formData.remark"
                :disabled="!data.editableSettings?.isCommentEditable"
                :placeholder="`${$t('driveBooking.remarkPlaceholder')}...`"
                :validators="commentValidation"
                :maxlength="200"
              />
            </div>

            <div class="u-mt-5">
              <CFlex justify="between">
                <h2>{{ $t("driveBooking.vehicle") }}</h2>
                <p v-if="data.driveBookingPartyName" class="fleet-source">
                  {{ $t("driveBooking.fleet") }}:
                  <span class="fleet-source-name">{{ data.driveBookingPartyName }}</span>
                </p>
              </CFlex>

              <CSelectedAssetDetails
                v-if="data.driveBookingAsset"
                @edit="handleCarSectionExpand"
                @cancel="cancelAssetChange"
                :name="data.driveBookingAsset?.description"
                :data="[
                  {
                    label: $t('driveBooking.identifier'),
                    value: data.driveBookingAsset.identifier,
                  },
                ]"
                :readonly="!data.editableSettings?.isRequestedTypeEditable"
                :isCancel="isCarSelectionExpanded"
              >
                <template #header>
                  <CFlex>
                    <CLicencePlate
                      v-if="licencePlateAdapter.registrationNumber"
                      :vehicle="licencePlateAdapter"
                    />
                    <h2 class="selected-asset-details__name">
                      {{ data.driveBookingAsset?.description }}
                    </h2>
                  </CFlex>
                </template>
              </CSelectedAssetDetails>
            </div>

            <div
              v-if="shouldShowCarSelection"
              class="edit-reservation__car-selection-wrapper"
            >
              <CDriveSelection
                :hideManualEntry="
                  data.driveBookingAsset?.requestedType !==
                    Number(DriveReservationRequestType.MANUAL)
                "
                ref="driveSelection"
                :data="driveBooking"
                :value="selectedEntityId"
                @change="handleCarChange"
                @licensePlateChange="handleLicensePlateChange"
                @modelChange="handleModelChange"
                :manualLicensePlate="formData.manual?.licensePlate"
                :manualCarModel="formData.manual?.carModel"
                :hasError="hasError"
              />
              <div
                v-if="shouldShowAssetWarningLabel"
                class="edit-reservation__tag-wrapper"
              >
                <CTag theme="warning-light">
                  <CIcon name="warning-alt" :size="25" />
                  {{
                    $t("driveBooking.switchedFrom", {
                      from: initialCarData.description,
                      to: getSelectedEntityDescription(),
                    })
                  }}
                </CTag>
              </div>
              <p v-if="hasError && !selectedEntityId" class="car-select-error-message">
                {{ $t("driveBooking.carSelectionErrorMessage") }}
              </p>
            </div>

            <CTag v-if="showNoAssetsWarning" theme="warning-light" class="u-mt-5">
              <CIcon :size="24" name="warning-alt" class="mr-1" />
              {{ $t("driveBooking.noAssetsAvailable") }}
            </CTag>

            <div v-if="data.status === DriveBookingStatus.RESERVED" class="u-mt-5">
              <CButton
                height="small"
                theme="main-danger"
                @click="() => DriveBookingModals.cancelReservation(data.id, driveContext)"
              >
                <CIcon name="delete-trash" />
                {{ $t("driveBooking.cancelReservationButton") }}
              </CButton>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <CIcon v-if="selectedEntityId" name="check-icon" />
        <p v-if="selectedEntityId">
          {{ $t("driveBooking.selected") }} {{ selectedDescription }}
        </p>

        <CButton
          v-if="
            data.status !== DriveBookingStatus.NO_SHOW &&
              data.status !== DriveBookingStatus.CANCELED
          "
          type="submit"
          theme="main-primary"
        >{{ $t("general.confirm") }}</CButton
        >
      </template>
    </CModal>
  </CForm>
</template>

<script setup lang="ts">
  import { reactive, ref, watch, watchEffect, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import moment from 'moment';
  import { PartialTimeObj } from '@vuepic/vue-datepicker';
  import CModal from '@/general/components/CModal.vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CField from '@/shared/components/form/CField.vue';
  import { maxLength, required } from '@/shared/utils/form/validation';
  import COptionsDropdown from '@/dossier/components/COptionsDropdown.vue';
  import DriveBookingClient from '@/driveBookings/client/DriveBookingClient';
  import DriveAvailableAssets from '@/driveBookings/models/DriveAvailableAssets';
  import DriveBookingService from '@/driveBookings/services/DriveBookingService';
  import DriveReservationRequestType from '@/driveBookings/constants/DriveReservationRequestType';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import ContactContext from '@/contact/models/ContactContext';
  import ContactModals from '@/contact/util/ContactModals';
  import DriveBookingModals from '../services/DriveBookingModals';
  import CSelectedAssetDetails from '../components/CSelectedAssetDetails.vue';
  import CLicencePlate from '@/shared/components/CLicencePlate.vue';
  import Vehicle from '@/vehicle/models/Vehicle';
  import CDriveSelection from '../components/CDriveSelection.vue';
  import { BasicModalResult,
           MODAL_CANCELLED,
           MODAL_CONFIRMED } from '@/general/utils/Modal';
  import DriveBookingUpdate from '../models/DriveBookingUpdate';
  import DriveBookingContext from '../models/DriveBookingContext';
  import CTag from '@/shared/components/CTag.vue';
  import { formatMoment } from '@/shared/converters/dateConverters';
  import AuthService from '@/general/services/AuthService';
  import { getDefaultCountryId } from '@/shared/models/Country';
  import DriveBookingStatus from '../constants/DriveBookingStatus';
  import CFlex from '@/shared/components/CFlex.vue';

  const props = defineProps<{
    driveContext: DriveBookingContext;
    contactContext: ContactContext;
  }>();

  const data = computed(() => props.driveContext.getSelectedDriveBooking());

  const licencePlateAdapter = computed(() => {
    return ({
      registrationNumber: data.value?.driveBookingAsset?.registrationNumber,
      countryId: AuthService.getCurrentUser().countryId ?? getDefaultCountryId(),
    } as unknown) as Vehicle;
  });

  const selectedEntityId = ref('');

  const driveBooking = ref<DriveAvailableAssets | null>(null);
  const hasError = ref(false);
  const showNoAssetsWarning = ref(false);

  const foundContact = props.contactContext.contacts.find(
    (c) => c.customer.dealerCustomerDataId === data.value.renter?.id,
  );

  const selectedDriver = ref();
  const initialDriver = ref();

  if (foundContact) {
    selectedDriver.value = {
      label: `${foundContact?.customer.firstName} ${foundContact?.customer.lastName}`,
      value: foundContact?.customer.dealerCustomerDataId ?? null,
    };

    initialDriver.value = {
      label: `${foundContact?.customer.firstName} ${foundContact?.customer.lastName}`,
      value: foundContact?.customer.dealerCustomerDataId ?? null,
    };
  }

  const i18n = useI18n();

  const driveSelection = ref(null);

  const isCarSelectionExpanded = ref(false);
  const isDriverSectionExpanded = ref(false);

  const groupTitlePlaceholder = computed(() =>
    i18n.t('driveBooking.groupTitlePlaceholder'),
  );
  const classTitlePlaceholder = computed(() =>
    i18n.t('driveBooking.classTitlePlaceholder'),
  );
  const assetTitlePlaceholder = computed(() =>
    i18n.t('driveBooking.assetTitlePlaceholder'),
  );

  function getAssetId() {
    if (
      data.value.driveBookingAsset?.requestedType
      === Number(DriveReservationRequestType.MANUAL)
    ) {
      return DriveReservationRequestType.MANUAL;
    }

    if (
      data.value.driveBookingAsset?.requestedType
      === Number(DriveReservationRequestType.GROUP)
    ) {
      return data.value.driveBookingAsset?.assetGroupId;
    }

    if (
      data.value.driveBookingAsset?.requestedType
      === Number(DriveReservationRequestType.CLASS)
    ) {
      return data.value.driveBookingAsset?.assetClassId;
    }

    return data.value.driveBookingAsset?.assetId;
  }

  const formData = reactive<{
    start: moment.Moment | null;
    end: moment.Moment | null;
    driverId: number | null;
    driveBookingFleetSourceId: number | null;
    entityId: number | null;
    requestedType: number | null;
    remark: string;
    manual: {
      licensePlate: string | undefined;
      carModel: string | undefined;
    } | null;
  }>({
    start: data.value.scheduledFrom,
    end: data.value.scheduledTill,
    driverId: data.value.renter?.id ?? null,
    driveBookingFleetSourceId: data.value.driveBookingFleetSourceId ?? null,
    entityId: getAssetId(),
    requestedType: data.value.driveBookingAsset?.requestedType ?? null,
    remark: data.value.comment,
    manual:
      data.value.driveBookingAsset?.requestedType
      === Number(DriveReservationRequestType.MANUAL)
        ? {
          licensePlate: data.value.driveBookingAsset?.registrationNumber,
          carModel: data.value.driveBookingAsset?.description,
        }
        : null,
  });

  const initialCarData = reactive<{
    description: string | null;
    entityId: number | null;
    requestedType: number | null;
    manual: {
      licensePlate: string;
      carModel: string;
    } | null;
  }>({
    description: data.value.driveBookingAsset?.description ?? '',
    entityId: getAssetId(),
    requestedType: data.value.driveBookingAsset?.requestedType ?? null,
    manual:
      data.value.driveBookingAsset?.requestedType
      === Number(DriveReservationRequestType.MANUAL)
        ? {
          licensePlate: data.value.driveBookingAsset?.registrationNumber,
          carModel: data.value.driveBookingAsset?.description,
        }
        : null,
  });

  const driveBookingContactsOptions = computed(() =>
    props.contactContext.contacts.map((contact) => {
      const option = {
        label: `${contact.customer.firstName || ''} ${
          contact.customer.lastName || ''
        }`.trim(),
        value: contact.customer.dealerCustomerDataId,
      };

      return option;
    }),
  );

  function handleCarSectionExpand() {
    isCarSelectionExpanded.value = true;

    if (
      isCarSelectionExpanded.value
      && Number(data.value.driveBookingAsset?.requestedType)
        === Number(DriveReservationRequestType.MANUAL)
    ) {
      selectedEntityId.value = DriveReservationRequestType.MANUAL;
      return;
    }

    if (
      isCarSelectionExpanded.value
      && Number(data.value.driveBookingAsset?.requestedType)
        === Number(DriveReservationRequestType.GROUP)
    ) {
      selectedEntityId.value = `${data.value.driveBookingAsset?.requestedType}-${data.value.driveBookingAsset?.assetGroupId}`;
    }

    if (
      isCarSelectionExpanded.value
      && Number(data.value.driveBookingAsset?.requestedType)
        === Number(DriveReservationRequestType.CLASS)
    ) {
      selectedEntityId.value = `${data.value.driveBookingAsset?.requestedType}-${data.value.driveBookingAsset?.assetClassId}`;
    }
  }

  function handleDriverSectionExpand() {
    isDriverSectionExpanded.value = true;
  }

  function isCarDataChanged() {
    if (formData.requestedType !== initialCarData.requestedType) {
      return true;
    }

    if (formData.entityId !== initialCarData.entityId) {
      return true;
    }

    if (formData.manual?.licensePlate !== initialCarData.manual?.licensePlate) {
      return true;
    }

    if (formData.manual?.carModel !== initialCarData.manual?.carModel) {
      return true;
    }

    return false;
  }

  function isDriverChanged() {
    if (selectedDriver.value) {
      return selectedDriver.value.value !== initialDriver.value?.value;
    }

    return false;
  }

  watch(
    () => formData.start,
    () => {
      if (!formData.start?.isBefore(formData.end)) {
        formData.end = null;

        if (formData.manual) {
          formData.manual.licensePlate = initialCarData.manual?.licensePlate;
          formData.manual.carModel = initialCarData.manual?.carModel;
        }

        isCarSelectionExpanded.value = false;
        selectedEntityId.value = '';
        driveBooking.value = null;
      }
    },
  );

  const minEndDate = computed(() => {
    if (formData.start) {
      return formData.start.clone();
    }

    return null;
  });

  const maxEndDate = computed(() => {
    if (!data.value.editableSettings?.isEndDateEditable && formData.end) {
      return formData.end.clone();
    }

    return null;
  });

  const endDateInternalValue = ref<Date | null>(null);

  const minEndDateTime = computed(() => {
    if (formData.start && minEndDate.value) {
      const currentInternalValue = endDateInternalValue.value
        ? moment(endDateInternalValue.value)
        : null;

      const isCurrentInternalDaySameAsStartDate = currentInternalValue?.isSame(
        formData.start,
        'day',
      );

      if (isCurrentInternalDaySameAsStartDate || !currentInternalValue) {
        return {
          hours: minEndDate.value.hour(),
          minutes: minEndDate.value.clone().add(5, 'minutes').minutes(),
        } as PartialTimeObj;
      }
    }

    return undefined;
  });

  const endDateStartTime = computed(() => {
    if (!minEndDate.value) {
      return undefined;
    }

    return {
      hours: minEndDate.value.hour(),
      minutes: minEndDate.value.clone().add(5, 'minutes').minutes(),
    } as PartialTimeObj;
  });

  function handleDateInternalValueChange(value: Date) {
    endDateInternalValue.value = value;
  }

  const emit = defineEmits<{
    (e: 'close', result: BasicModalResult): void;
  }>();

  async function cancelAssetChange() {
    if (isCarDataChanged()) {
      const value = await DriveBookingModals.cancelChange(
        i18n.t('driveBooking.cancelAssetChangeTitle'),
        i18n.t('driveBooking.cancelAssetChangeContent'),
      );

      if (value === MODAL_CONFIRMED) {
        isCarSelectionExpanded.value = false;
        selectedEntityId.value = '';
      }
    } else {
      isCarSelectionExpanded.value = false;
      showNoAssetsWarning.value = false;
    }
  }

  async function cancelDriverChange() {
    if (isDriverChanged()) {
      const value = await DriveBookingModals.cancelChange(
        i18n.t('driveBooking.cancelDriveChangeTitle'),
        i18n.t('driveBooking.cancelDriveChangeContent'),
      );

      if (value === MODAL_CONFIRMED) {
        isDriverSectionExpanded.value = false;
        selectedDriver.value = initialDriver.value;
      }
    } else {
      isDriverSectionExpanded.value = false;
    }
  }

  function getSelectedEntityDescription() {
    if (selectedEntityId.value === DriveReservationRequestType.MANUAL) {
      return 'Manual';
    } else if (selectedEntityId.value) {
      const [type, id] = selectedEntityId.value.split('-', 2);
      if (type === DriveReservationRequestType.CLASS) {
        const foundClass = driveBooking.value?.classes.find((cls) => cls.id === Number(id));
        return foundClass?.description;
      } else if (type === DriveReservationRequestType.ASSET) {
        return driveBooking.value?.assets.find((asset) => asset.id === Number(id))
          ?.description;
      } else {
        return driveBooking.value?.groups.find((asset) => asset.id === Number(id))
          ?.description;
      }
    }
    return null;
  }

  const selectedDescription = computed(() => {
    return getSelectedEntityDescription();
  });

  function handleCarChange(car: string) {
    selectedEntityId.value = car;
  }

  function handleLicensePlateChange(licensePlate: string) {
    if (formData.manual) {
      formData.manual.licensePlate = licensePlate;
    }
  }

  function handleModelChange(model: string) {
    if (formData.manual) {
      formData.manual.carModel = model;
    }
  }

  async function getAvailableAssets() {
    if (
      !formData.start
      || !formData.end
      || !formData.driveBookingFleetSourceId
      || !isCarSelectionExpanded.value
    ) {
      return;
    }

    const startDateUtc = formatMoment(formData.start);
    const endDateUtc = formatMoment(formData.end);
    const response = await DriveBookingService.getAvailableAssets(
      startDateUtc,
      endDateUtc,
      formData.driveBookingFleetSourceId,
    );

    const editedData = {
      ...response,
      groups: response?.groups?.map((group) => ({
        ...group,
        description: group.description || groupTitlePlaceholder.value,
      })),
      classes: response?.classes?.map((driveClass) => ({
        ...driveClass,
        description: driveClass.description || classTitlePlaceholder.value,
      })),
      assets: response?.assets?.map((asset) => ({
        ...asset,
        description: asset.description || assetTitlePlaceholder.value,
      })),
    };

    if (
      response.assets.length === 0
      && response.classes.length === 0
      && response.groups.length === 0
    ) {
      showNoAssetsWarning.value = true;
    } else {
      showNoAssetsWarning.value = false;
    }

    driveBooking.value = editedData;
  }

  const shouldShowAssetWarningLabel = computed(
    () =>
      isCarSelectionExpanded.value
      && isCarDataChanged()
      && !(
        Number(data.value.driveBookingAsset?.requestedType)
        === Number(DriveReservationRequestType.MANUAL)
      ),
  );

  const shouldShowDriverWarningLabel = computed(
    () => isDriverSectionExpanded.value && isDriverChanged(),
  );

  watchEffect(getAvailableAssets);

  watch(selectedEntityId, (newValue) => {
    if (newValue) {
      if (newValue === DriveReservationRequestType.MANUAL) {
        formData.requestedType = Number(selectedEntityId.value);
        formData.manual = {
          licensePlate: formData.manual?.licensePlate,
          carModel: formData.manual?.carModel,
        };
      } else {
        const [type, id] = newValue.split('-', 2);
        formData.entityId = Number(id);
        formData.requestedType = Number(type);
      }
    } else {
      formData.entityId = initialCarData.entityId;
      formData.requestedType = initialCarData.requestedType;
      formData.manual = initialCarData.manual;
    }
  });

  const submit = async () => {
    const dataToSubmit: DriveBookingUpdate = {};

    if (formData.start !== data.value.scheduledFrom) {
      dataToSubmit.startDate = formData.start ? formatMoment(formData.start) : null;
    }
    if (formData.end !== data.value.scheduledTill) {
      dataToSubmit.endDate = formData.end ? formatMoment(formData.end) : null;
    }
    if (formData.driverId !== data.value.renter?.id) {
      dataToSubmit.driverId = formData.driverId;
    }

    if (formData.remark !== data.value.comment) {
      dataToSubmit.comment = formData.remark?.trim();
    }

    if (
      formData.entityId !== getAssetId()
      || (formData.entityId === getAssetId()
        && formData.requestedType !== data.value.driveBookingAsset?.requestedType)
    ) {
      dataToSubmit.requestedType = formData.requestedType;
      dataToSubmit.entityId = formData.entityId;
    }

    if (formData.requestedType !== data.value.driveBookingAsset?.requestedType) {
      dataToSubmit.requestedType = formData.requestedType;
    }

    if (
      formData.requestedType === Number(DriveReservationRequestType.MANUAL)
      && formData.manual
      && (formData.manual.licensePlate !== initialCarData.manual?.licensePlate
        || formData.manual.carModel !== initialCarData.manual?.carModel)
    ) {
      dataToSubmit.requestedType = Number(DriveReservationRequestType.MANUAL);
      dataToSubmit.manual = {
        registrationNumber: formData.manual.licensePlate,
        description: formData.manual.carModel,
      };
    }

    if (Object.keys(dataToSubmit).length === 0) {
      close();
      return;
    }

    await DriveBookingClient.update(data.value.id, dataToSubmit);
    await DriveBookingService.loadDetails(data.value.id, props.driveContext);
    emit('close', MODAL_CONFIRMED);
  };

  function close() {
    emit('close', MODAL_CANCELLED);
  }

  watch([selectedDriver], ([driver]) => {
    if (driver) {
      formData.driverId = driver.value;
    } else {
      formData.driverId = null;
    }
  });

  const startDateValidation = {
    required,
  };

  const endDateValidation = {
    required,
    isAfterStartDate: (value: moment.Moment) => {
      if (formData.start && value) {
        return value.isAfter(formData.start);
      }

      return true;
    },
  };

  const commentValidation = {
    maxLength: maxLength(200),
  };

  const shouldShowCarSelection = computed(() => {
    return (
      isCarSelectionExpanded.value
      && (Number(data.value.driveBookingAsset?.requestedType)
        === Number(DriveReservationRequestType.MANUAL)
        || (driveBooking.value
          && (driveBooking.value?.groups.length > 0
            || driveBooking.value?.classes.length > 0
            || driveBooking.value?.assets.length > 0)))
    );
  });
</script>

<style lang="scss" scoped>
.message-wrapper {
  display: flex;
  padding: 30px;
  align-items: center;
  justify-content: center;
}

.drive-reservation-modal-wrapper {
  padding-bottom: 1.5rem;
}

.facturation-wrapper {
  display: flex;
  gap: 10px;
}

.facturation-wrapper > * {
  flex: 1 50%;
}

.datetime-wrapper {
  display: flex;
  gap: 12px;
  padding: 12px 1rem 0px 1rem;
  box-shadow: 0px 2px 13px 0px rgba(0, 0, 0, 0.3);
  position: sticky;
  top: 0px;
  background-color: $neutral-50;
  z-index: 2;
}

.drive-form-wrapper {
  padding-inline: 1rem;
}

.datetime-wrapper > * {
  flex: 1;
}

.driver-dropdown-wrapper {
  flex: 1;
}

.driver-dropdown-container {
  margin-top: 20px;
}

.contact-button-wrapper {
  width: 200px;
  display: flex;
  flex-direction: column;
}

.fleet-source {
  color: $neutral-900;
}

.fleet-source-name {
  font-weight: bold;
}

.car-select-error-message {
  color: $danger;
}

.edit-reservation__car-selection-wrapper {
  padding-top: 20px;
}

.edit-reservation__tag-wrapper {
  margin-top: 15px;
}

:deep(.c-modal-body) {
  background-color: $neutral-50;
}

:deep(.dp__disabled) {
  background-color: $neutral-300;
}

:deep(.dp__input_icon) {
  color: $neutral-900;
}

:deep(.c-field-datetime.c-field-disabled) {
  .dp__input,
  .dp__input_icon {
    cursor: not-allowed;
  }
}

:deep(.c-field) {
  margin-top: 0px;
}
</style>
